/**
 * The first step of every import dialog: a drop-target-styled file picker,
 * the parse error if one occurred, and whatever template links the caller
 * wants underneath.
 */
import type { ReactNode, RefObject } from "react";
import { Upload } from "lucide-react";

interface FilePickStepProps {
  /** DOM id tying the label to the input — unique per dialog. */
  inputId: string;
  /** `accept` attribute for the file input. */
  accept: string;
  /** Primary call to action, e.g. "Choose a CSV or XLSX file". */
  label: string;
  /** Secondary line describing what the file must look like. */
  hint: string;
  parseError: string | null;
  inputRef: RefObject<HTMLInputElement | null>;
  onFile: (file: File) => void;
  /** Template download links and any explanatory copy below the picker. */
  children?: ReactNode;
}

export function FilePickStep({
  inputId,
  accept,
  label,
  hint,
  parseError,
  inputRef,
  onFile,
  children,
}: FilePickStepProps) {
  return (
    <div className="mt-4 space-y-3">
      <label
        htmlFor={inputId}
        className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-line bg-bg px-6 py-10 text-center hover:bg-raised"
      >
        <Upload size={20} className="text-muted" aria-hidden />
        <span className="text-sm font-medium text-ink">{label}</span>
        <span className="text-xs text-muted">{hint}</span>
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={accept}
          className="sr-only"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onFile(file);
          }}
        />
      </label>
      {parseError ? <p className="text-xs text-bad">{parseError}</p> : null}
      {children}
    </div>
  );
}
